import React, { useCallback, useMemo, useState } from 'react';
import { Card, Placeholder } from "react-bootstrap";
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js';

import { 
  activityConfig, 
  getActivityChartData, 
  getActivityChartOptions, 
  getNumericsChartData, 
  getNumericsChartOptions, 
  getWaveformChartData, 
  getWaveformChartOptions, 
  numericsConfigs,
  waveformConfigs 
} from './VitalSignStyles';
import { NumericalVitalData, WaveformVitalData, NumericsDataPoint } from './VitalDataTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

interface VitalSignsProps {
  numerics: NumericalVitalData | null;  // Made nullable
  rawData: WaveformVitalData | null;    // Made nullable
  isLoadingNumerics: boolean;
  isLoadingRawData: boolean;
  onTimeSelect: (epochTimeSec: number) => void;
  selectedDate: Date | null;            // Added selectedDate prop
}

const VitalSigns: React.FC<VitalSignsProps> = ({ 
  numerics: vitalData, 
  rawData, 
  isLoadingNumerics,
  isLoadingRawData, 
  onTimeSelect,
  selectedDate 
}) => {
  const [hasSelectedTime, setHasSelectedTime] = useState(false);

  const timeRange = useMemo(() => {
    if (!vitalData) return null;

    const timestamps = [
      ...vitalData.heartRate.map(d => new Date(d.timestamp).getTime()),
      ...vitalData.respiratoryRate.map(d => new Date(d.timestamp).getTime()),
      ...vitalData.temperature.map(d => new Date(d.timestamp).getTime()),
      ...vitalData.steps.map(d => new Date(d.timestamp).getTime()),
      ...vitalData.eda.map(d => new Date(d.timestamp).getTime()),
      ...vitalData.prv.map(d => new Date(d.timestamp).getTime()),
      ...vitalData.activity.map(d => new Date(d.timestamp).getTime())
    ];

    if (timestamps.length === 0) return null;

    const min = new Date(Math.min(...timestamps));
    const max = new Date(Math.max(...timestamps));
    const timeScale = Array.from({ length: 11 }, (_, i) => 
      new Date(min.getTime() + ((max.getTime() - min.getTime()) * i) / 10)
    );
    
    return { min, max, timeScale };
  }, [vitalData]);

  const handleChartClick = useCallback((event: any, elements: any[], chart: any) => {
    if (elements.length > 0) {
      const clickedElement = elements[0];
      const xValue = chart.scales.x.getValueForPixel(clickedElement.element.x);
      const epochTimeSec = Math.floor(xValue / 1000);
      setHasSelectedTime(true);
      onTimeSelect(epochTimeSec);
    }
  }, [onTimeSelect]);

  const getChartOptionsWithClick = useCallback((
    vitalType: keyof typeof numericsConfigs | 'activity',
    minTime: number,
    maxTime: number
  ) => {
    const baseOptions = vitalType === 'activity' 
      ? getActivityChartOptions(minTime, maxTime)
      : getNumericsChartOptions(vitalType, minTime, maxTime);

    return {
      ...baseOptions,
      onClick: handleChartClick
    };
  }, [handleChartClick]);

  const isActivity = (vital: string): vital is 'activity' => vital === 'activity';

  const numericsToDisplay: (keyof NumericalVitalData)[] = [
    "heartRate", "respiratoryRate", "temperature", "steps", "eda", "prv"
  ];

  // If no date is selected, show only a message
  if (!selectedDate) {
    return (
      <Card>
        <Card.Body>
          <div className="text-center text-gray-600 py-8">
            <h5>Select a date to view vital signs data</h5>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // If data is loading, show loading state
  if (isLoadingNumerics) {
    return (
      <Card>
        <Card.Body>
          <h4 className="mb-4">Vital Signs for {selectedDate.toLocaleDateString()}</h4>
          <div className="d-flex justify-content-center py-8">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // If no data available for selected date
  if (!vitalData || !timeRange) {
    return (
      <Card>
        <Card.Body>
          <h4 className="mb-4">Vital Signs for {selectedDate.toLocaleDateString()}</h4>
          <div className="text-center text-gray-600 py-8">
            <p>No vital signs data available for this date</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // Main content with vital signs and waveforms
  return (
    <Card>
      <Card.Body>
        <h4 className="mb-4">Vital Signs for {selectedDate.toLocaleDateString()}</h4>
        <div className="space-y-12 w-full">
          {/* Numerics Plots */}
          {numericsToDisplay.map((numeric) => {
            if (isActivity(numeric)) return null;
            
            return (
              <div key={numeric} className="mb-4">
                <h6 className="text-gray-700 mb-2">{numericsConfigs[numeric].title}</h6>
                <div style={{ height: '240px', width: '100%', paddingRight: '24px', cursor: 'pointer' }}>
                  <Line 
                    key={numeric}
                    data={getNumericsChartData(numeric, vitalData[numeric] as NumericsDataPoint<number>[])}
                    options={getChartOptionsWithClick(numeric, timeRange.min.getTime(), timeRange.max.getTime())}
                  />
                </div>
              </div>
            );
          })}

          {/* Activity Plot */}
          {vitalData.activity && vitalData.activity.length > 0 && (
            <div className="mb-4">
              <h6 className="text-gray-700 mb-2">{activityConfig.title}</h6>
              <div style={{ height: '240px', width: '100%', paddingRight: '24px', cursor: 'pointer' }}>
                <Line 
                  data={getActivityChartData(vitalData.activity)}
                  options={getChartOptionsWithClick('activity', timeRange.min.getTime(), timeRange.max.getTime())}
                />
              </div>
            </div>
          )}

          {/* Instructions when no time selected */}
          {!hasSelectedTime && (
            <Card className="mt-4">
              <Card.Body>
                <div className="text-center text-gray-600 py-8">
                  <h5>Click on any point in the plots above to view waveform data</h5>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Waveform Plots */}
          {hasSelectedTime && rawData?.timestamp && (
            <div className="mt-4">
              <h6 className="text-gray-700 mb-4">Waveform Data for Selected Time</h6>
              {isLoadingRawData ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '240px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : rawData && (
                <>
                  {/* BVP Waveform */}
                  <div className="mb-4">
                    <h6 className="text-gray-700 mb-2">
                      {waveformConfigs.bvp.title} - {new Date(rawData.timestamp * 1000).toLocaleTimeString()}
                    </h6>
                    <div style={{ height: '240px', width: '100%' }}>
                      <Line
                        data={getWaveformChartData('bvp', rawData.bvp)}
                        options={getWaveformChartOptions('bvp')}
                      />
                    </div>
                  </div>

                  {/* EDA Waveform */}
                  <div className="mb-4">
                    <h6 className="text-gray-700 mb-2">
                      {waveformConfigs.eda.title} - {new Date(rawData.timestamp * 1000).toLocaleTimeString()}
                    </h6>
                    <div style={{ height: '240px', width: '100%' }}>
                      <Line
                        data={getWaveformChartData('eda', rawData.eda)}
                        options={getWaveformChartOptions('eda')}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

export default VitalSigns;